/**
 * AutoGradeStatusBadge — Inline badge showing auto-grade status.
 * LAUNCH:C6.P3
 */
import React from 'react';
import { Check, X, Clock, Lock, Loader2 } from 'lucide-react';

const STATUS_CONFIG = {
  pending: {
    icon: Clock,
    label: 'Pending',
    dotClass: 'bg-yellow-400',
    textClass: 'text-yellow-400',
  },
  processing: {
    icon: Loader2,
    label: 'Grading...',
    dotClass: 'bg-blue-400',
    textClass: 'text-blue-400',
    animate: true,
  },
  completed: {
    icon: Check,
    label: 'Graded',
    dotClass: 'bg-green-400',
    textClass: 'text-green-400',
  },
  failed: {
    icon: X,
    label: 'Failed',
    dotClass: 'bg-red-400',
    textClass: 'text-red-400',
  },
  quota_exceeded: {
    icon: Lock,
    label: 'Quota',
    dotClass: 'bg-orange-400',
    textClass: 'text-orange-400',
  },
};

export default function AutoGradeStatusBadge({ status, gradeValue }) {
  if (!status) return null;

  const config = STATUS_CONFIG[status];
  /* c8 ignore next */
  if (!config) return null;

  const Icon = config.icon;
  // ⛔ THE SERVED SHAPE IS A STRING. VaultGrid passes item.auto_grade_value, which this PR
  // introduces as Optional[Decimal] -- and pydantic v2 serialises Decimal to a JSON STRING
  // ("9.4", not 9.4). gradeValue.toFixed therefore threw "is not a function" on EVERY completed
  // item, and because the badge renders inside the grid cell it took the whole vault grid's
  // render down, not one badge.
  //
  // ⚠ IT WAS INVISIBLE BEFORE THIS PR AND UNTESTABLE AFTER. On development the field does not
  // exist yet, so gradeValue is undefined, the `!= null` guard short-circuits, and nothing
  // renders a number -- the crash arrives only when the schema change lands. The fixtures
  // passed numbers, so the suite could not see it either.
  //
  // Number() is a no-op on a real number, and the NaN check keeps a malformed value rendering
  // the status label instead of "NaN".
  const gradeNum = gradeValue == null || Number.isNaN(Number(gradeValue)) ? null : Number(gradeValue);
  const displayLabel = status === 'completed' && gradeNum != null
    ? gradeNum.toFixed(1)
    : config.label;

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${config.textClass} bg-zinc-800/80`}
      aria-label={`Auto-grade status: ${config.label}`}
    >
      <Icon
        className={`w-3 h-3 ${config.animate ? 'animate-spin' : ''}`}
        aria-hidden="true"
      />
      <span>{displayLabel}</span>
    </span>
  );
}
